import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import { find, getParent, hasClass, removeClass, addClass } from '@fretadao/f-js-dom';
import { isPresent } from '@fretadao/f-js-utils';
import { decode } from '@here/flexpolyline';

import purplemarker from 'images/icons/map/purplepinkpin80x64.png';

import L from 'leaflet';
import 'leaflet.gridlayer.googlemutant';
import 'leaflet-providers/leaflet-providers';
import 'leaflet.markercluster/dist/leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'packs/leaflet.forceZIndex';
import 'leaflet-sidebar-v2';

application.register('b2b-routing-map', class extends Controller {
  static targets = ['mapContainer'];
  static values = {
    readonly: {
      type: String,
      default: 'false'
    },
    waypoints: {
      type: Array,
      default: []
    },
    polylines: {
      type: Array,
      default: []
    },
    mobileView: {
      type: Boolean,
      default: true
    }
  };

  INIT_COORDINATES = L.latLng(-23.6821604, -46.8754915);
  INIT_ZOOM = 14;
  MIN_ZOOM = 6;
  MAX_ZOOM = 18;
  ACTIVE_LINE_CARD_CLASSES = ['border-blue-500'];
  ACTIVE_COLOR = '#0052CC';
  DISABLED_COLOR = '#323232';
  ACTIVE_LINE_Z = 20000;
  DISABLED_LINE_Z = 10000;

  MOBILE_BREAKPOINT = 640; // same as tailwind, '@media (width >= 40rem) {...}' 640px == 40rem

  map = null;

  connect() {
    this.#configMap();

    if (this.readonlyValue == 'true' && [this.waypointsValue, this.polylinesValue].every(isPresent)) {
      this.showTrip();
    }

    if (this.mobileViewValue) {
      this.#setSidebarPosition();
      this.#setMobileListeners();
    }
  }

  disconnect() {
    this.map.eachLayer((layer) => layer.remove());
    this.map.remove();
    this.map = null;

    if (this.mobileViewValue) this.#removeMobileListeners();
  }

  showTrip() {
    this.sidebar.remove();
    this.plotReadOnlyWaypoints();
    this.plotPolylines(this.polylinesValue);
  }

  plotWaypoints({ detail }) {
    this.#clearMarkers();

    const waypoints = detail;

    waypoints.forEach((wp) => {
      const ll = wp.coords;
      const marker = L.marker(ll, {
        icon: L.icon({
          iconUrl: purplemarker,
          zIndex: 11000,
          iconSize: [32, 40],
          iconAnchor: [16, 40]
        }),
        draggable: true,
        title: wp.title,
        fieldElId: wp.identifier
      });

      marker.on('dragstart', (event) => {
        event.target._oldLatLng = isPresent(event.target._oldLatLng) ? event.target._oldLatLng : [];
        event.target._oldLatLng.push(event.target._latlng);
      });

      marker.on('dragend', (event) => {
        const customRoutingControllerEl = find('[data-b2b-admin-custom-routing-target="routeInfoWrapper"]');

        customRoutingControllerEl.dispatchEvent(new CustomEvent('locatePosition', {
          detail: {
            marker: marker,
            address: wp.title,
            old: marker._oldLatLng,
            new: marker._latlng
          }
        }));
      });

      this.map._routingMarkers.push(marker);
      marker.addTo(this.map);
    });

    if (this.#routePlotable()) {
      this.map.fitBounds(L.latLngBounds(this.map._routingMarkers.map((marker) => marker._latlng)));
    } else if (isPresent(waypoints[0])) {
      this.#centerMapAt(waypoints[0].coords);
    }
  }

  plotReadOnlyWaypoints() {
    this.waypointsValue.forEach((wp) => {
      const ll = wp.coords;
      const marker = L.marker(ll, {
        icon: L.icon({
          iconUrl: purplemarker,
          zIndex: 11000,
          iconSize: [32, 40],
          iconAnchor: [16, 40]
        }),
        draggable: false
      });

      marker.bindTooltip(L.tooltip({
        permanent: true,
        opacity: 1,
        content: `
          <div class="text-center">
            <b>Parada #${wp.index}</b>
            <br />
            <p class="">
              ${wp.address}
            </p>
          </div>
        `
      }));

      this.map._routingMarkers.push(marker);
      marker.addTo(this.map);
    });

    if (this.#routePlotable()) {
      this.map.fitBounds(L.latLngBounds(this.map._routingMarkers.map((marker) => marker._latlng)));
    } else if (waypoints[0]) {
      this.#centerMapAt(waypoints[0].coords);
    }
  }

  plotPolylineRoute({ detail }) {
    const polylines = detail;
    this.plotPolylines(polylines);
  }

  plotPolylines(encodedPolylines) {
    this.#clearPolylines();

    const decodedPolylines = encodedPolylines.map((encodedPolyline) => {
      return decode(encodedPolyline);
    });

    decodedPolylines.forEach(({ polyline }) => {
      const leafletPolyline = L.polyline(polyline, {
        interactive: false,
        smoothFactor: 1.5,
        stroke: true,
        color: '#BF36FF'
      });

      this.map._plottedPolylines.push(leafletPolyline);
      leafletPolyline.addTo(this.map);
    });
  }

  #setMobileListeners() {
    window.addEventListener('resize', (_event) => {
      const vpWidth = window.innerWidth || document.documentElement.clientWidth;

      this.#setSidebarPosition(vpWidth);
    });
  }

  #setSidebarPosition(viewportWidth = window.innerWidth) {
    const controllerParent = getParent(this.element);
    const sidebarContentEl = find('.RoutingMap-sidebarContentWrapper', controllerParent);

    if (viewportWidth < this.MOBILE_BREAKPOINT) {
      // moves sidebar content out of the map
      const outsideAnchorEl = find('.RoutingMap-outsideContentWrapper', controllerParent);

      outsideAnchorEl.appendChild(sidebarContentEl);
      this.sidebar.removeFrom(this.map);
    } else if (!sidebarContentEl.closest('[data-controller="b2b-routing-map"]')) {
      // moves sidebar content into the map
      const sidebarContentWrapper = find('.leaflet-sidebar-content', this.sidebar.getContainer());

      sidebarContentWrapper.appendChild(sidebarContentEl);
      this.sidebar.addTo(this.map);
    }
  }

  #removeMobileListeners() {
    window.removeEventListener('resize', (_event) => {
      const vpWidth = window.innerWidth || document.documentElement.clientWidth;

      this.#setSidebarPosition(vpWidth);
    });
  }

  #routePlotable() {
    return this.map._routingMarkers.length >= 2;
  }

  #configMap() {
    if (isPresent(this.map)) return;

    this.map = L.map(this.mapContainerTarget, {
      zoomControl: false,
      scrollWheelZoom: true,
      tap: false,
      touchZoom: true,
      doubleClickZoom: false,
      contextmenu: true,
    });

    this.#setLayers();
    this.#setSidebar();

    this.map.setView(this.INIT_COORDINATES, this.INIT_ZOOM);

    L.control.zoom({ position: 'topright' }).addTo(this.map);
    this.map.options.minZoom = this.MIN_ZOOM;
    this.map.options.maxZoom = this.MAX_ZOOM;

    this.mapContainerTarget.height = getParent(this.mapContainerTarget).style.height

    this.commonPane = this.map.createPane('commonPane');
    this.activePane = this.map.createPane('activePane');
    this.activePane.style.zIndex = 9999;

    this.map._routingMarkers = [];
    this.map._plottedPolylines = [];
  }

  #setSidebar() {
    this.sidebar = L.control.sidebar({
      closeButton: false,
      container: 'sidebar',
    });

    this.sidebar.addTo(this.map);
  }

  #setLayers() {
    // Due Google Maps API the property styles needs to receive an Array.
    const roadMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'roadmap',
      styles: []
    });

    const satMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'satellite',
      styles: []
    });

    const hybridMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'hybrid',
      styles: []
    });

    const transitMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'roadmap',
      styles: []
    });

    const trafficMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'roadmap',
      styles: []
    });

    transitMutant.addGoogleLayer('TransitLayer');
    trafficMutant.addGoogleLayer('TrafficLayer');

    const osm = L.tileLayer.provider('OpenStreetMap');
    const mapnik = L.tileLayer.provider('OpenStreetMap.Mapnik');

    const layers = {
      'Google Roadmap': roadMutant,
      'Google Roadmap Traffic': trafficMutant,
      'Google Roadmap Satellite': hybridMutant,
      'Google Transit': transitMutant,
      'Google Satellite': satMutant,
      'Mapnik': mapnik,
      'OpenStreetMaps': osm
    };

    roadMutant.addTo(this.map);

    this.control = L.control.layers(layers, {}, { position: 'bottomright' })
    this.control.addTo(this.map);
  }

  #clearMarkers() {
    if (this.map._routingMarkers.length != 0) {
      this.map._routingMarkers.forEach((oldMarker) => oldMarker.removeFrom(this.map));
      this.map._routingMarkers = [];
    }
  }

  #clearPolylines() {
    if (isPresent(this.map._plottedPolylines)) {
      this.map._plottedPolylines.forEach((polylineLayer) => polylineLayer.removeFrom(this.map));
    }
  }

  #centerMapAt(coords) {
    this.map.setView(coords, 20, { animation: true });
  }
});
